import { FC, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
	Alert,
	Box,
	Button,
	Chip,
	DialogActions,
	Divider,
	Paper,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import {
	FieldArray,
	FieldArrayRenderProps,
	Form,
	Formik,
	FormikHelpers,
} from "formik";

import { Option } from "@/components/Application/HtmlElements";
import { Translate } from "@/components/Application/Translate";
import { WizardChecklistValidationScheme } from "@/components/Procedures/NewChecklist/Validation/NewChecklistValidation";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";
import { Spinner } from "@/helpers/components/Spinner";

import {
	DocumentImportErrorCodes,
	DocumentImportPlan,
	ImportAction,
} from "@/model/Procedure/DocumentImportPlan";
import { DocumentMetadata } from "@/model/Procedure/DocumentMetadata";
import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { UserContext } from "@/model/User/UserContext";

import { ImportPlanFromWizard } from "@/service/ProcedureMutations";
import { GetImportPlan } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

interface NewWizardChecklistFormProps {
	onClose: () => void;
	selectedGroup: ProcedureDetailNode;
	setActiveSchemeId: (schemeId: number) => void;
}

const steps = [
	{
		label: "Checklist name*",
		translationKey: "Procedure.Checklist.New.Wizard.Name",
		content: (
			values: DocumentImportPlan,
			arrayHelpers: FieldArrayRenderProps,
			checklist: DocumentImportPlan | undefined,
		) => (
			<StepOne
				values={values}
				arrayHelpers={arrayHelpers}
				checklist={checklist}
			/>
		),
	},
	{
		label: "Checklist subheader (optional)",
		translationKey: "Procedure.Checklist.New.Wizard.Subheader",
		content: (
			values: DocumentImportPlan,
			arrayHelpers: FieldArrayRenderProps,
			checklist: DocumentImportPlan | undefined,
		) => (
			<StepTwo
				values={values}
				arrayHelpers={arrayHelpers}
				checklist={checklist}
			/>
		),
	},
	{
		label: "Checklist table*",
		translationKey: "Procedure.Checklist.New.Wizard.Table",
		content: (
			values: DocumentImportPlan,
			arrayHelpers: FieldArrayRenderProps,
			checklist: DocumentImportPlan | undefined,
		) => (
			<StepThree
				values={values}
				arrayHelpers={arrayHelpers}
				checklist={checklist}
			/>
		),
	},
];

const NewWizardChecklistForm: FC<NewWizardChecklistFormProps> = ({
	onClose,
	selectedGroup,
	setActiveSchemeId,
}) => {
	const user = useContext(UserContext);
	const [activeStep, setActiveStep] = useState(0);
	const { id } = useParams<RouteParams>();
	const importPlanFromWizard = ImportPlanFromWizard();
	const { data: checklist, isLoading } = GetImportPlan(
		true,
		"getimportplan",
		0,
		+selectedGroup.SchemeId,
	);

	const navigate = useNavigate();
	const handleNext = (
		index: number,
		values: DocumentImportPlan,
		setFieldTouched: (field: string, touched?: boolean) => void,
	) => {
		if (index === 0 && values.SchemeName === "") {
			setFieldTouched("SchemeName", true);
		} else if (
			index === 2 &&
			!values.Chapters?.some((chapter) => chapter.SelectedBody)
		) {
			setFieldTouched("Chapters", true);
		} else {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	const handleSubmit = async (
		values: DocumentImportPlan,
		formikHelpers: FormikHelpers<DocumentImportPlan>,
	) => {
		try {
			const response = await importPlanFromWizard(values);
			setActiveStep(0);
			AlertRequest.success();
			values.Chapters.forEach((chapter) => {
				chapter.SelectedHeader = false;
				chapter.SelectedBody = false;
			});
			// setActiveSchemeId(response.SchemeId);
		} catch (error) {
			AlertRequest.failed();
		} finally {
			onClose();
		}
	};

	const getSubheaderNames = (chapters: Array<DocumentMetadata>) => {
		if (!chapters) return [];
		const namesArr = chapters
			.filter((chapter) => chapter.FieldType === 0 && chapter.SelectedHeader)
			.map((chapter) => chapter.ChapterNr + " " + chapter.ChapterName);

		return namesArr.slice(0, 3).join(" | ") + "...";
	};

	const getChecklistName = (chapters: Array<DocumentMetadata>) => {
		if (!chapters) return [];
		const namesArr = chapters
			.filter((chapter) => chapter.FieldType !== 0 && chapter.SelectedBody)
			.map((chapter) => "Table " + chapter.ChapterNr);
		return namesArr.join("");
	};

	if (isLoading || !checklist || !user) {
		return <Spinner text="Loading" />;
	}

	return (
		<Formik
			initialValues={{
				ProcedureId: +id,
				NodeSchemeId: selectedGroup.SchemeId,
				NodeId: 0,
				ParentId: selectedGroup.TreeId,
				OrganisationId: user.CompanyId,
				SchemeId: 0,
				SchemeName: "",
				NodeName: selectedGroup.Name,
				ProcedureName: selectedGroup.Name,
				UserId: user.Id,
				Revision: 0,
				NewRevision: 0,
				FileName: selectedGroup.FileName,
				DocumentId: checklist.DocumentId,
				GroupId: -1,
				Action: ImportAction.Ok,
				StatusCode: DocumentImportErrorCodes.NewPage,
				Chapters: checklist?.Chapters,
				SortNumber: selectedGroup.Children.length + 1,
			}}
			onSubmit={handleSubmit}
			validationSchema={WizardChecklistValidationScheme}
			validateOnBlur
		>
			{({ values, errors, setFieldTouched, isSubmitting }) => (
				<Form>
					<Box sx={{ minWidth: 400, margin: "0 auto", padding: 2 }}>
						<Stepper activeStep={activeStep} orientation="vertical">
							{steps.map((step, index) => (
								<Step key={step.label}>
									<StepLabel
										error={
											(index === 0 && Boolean(errors.SchemeName)) ||
											(index === 2 && Boolean(errors.Chapters))
										}
										optional={
											index === 0
												? values.SchemeName
												: index === 1
													? getSubheaderNames(values.Chapters)
													: getChecklistName(values.Chapters)
										}
									>
										<Translate String={step.translationKey}>
											{step.label}
										</Translate>
									</StepLabel>
									<StepContent>
										<FieldArray
											name="Chapters"
											render={(arrayHelpers) =>
												step.content(values, arrayHelpers, checklist)
											}
										/>
										<Box sx={{ mb: 2 }}>
											<Button
												variant="contained"
												color="success"
												onClick={() =>
													handleNext(index, values, setFieldTouched)
												}
												sx={{ mt: 1, mr: 1 }}
											>
												{index === steps.length - 1 ? (
													<Translate String="Procedure.Checklist.New.Wizard.Finish">
														Finish
													</Translate>
												) : (
													<Translate String="Procedure.Checklist.New.Wizard.Continue">
														Continue
													</Translate>
												)}
											</Button>
											<Button
												variant="outlined"
												color="secondary"
												disabled={index === 0}
												onClick={handleBack}
												sx={{ mt: 1, mr: 1 }}
											>
												<Translate String="Procedure.Checklist.New.Wizard.Back">
													Back
												</Translate>
											</Button>
										</Box>
									</StepContent>
								</Step>
							))}
						</Stepper>
						{activeStep === steps.length && (
							<Paper square elevation={0} sx={{ p: 3 }}>
								<Typography>
									<Alert
										icon={<CheckIcon fontSize="inherit" />}
										severity="success"
									>
										<Translate String="Procedure.Checklist.New.Wizard.Completed">
											All steps completed - you're finished
										</Translate>
									</Alert>
								</Typography>
								<Button
									variant="outlined"
									color="warning"
									onClick={handleReset}
									sx={{ mt: 1, mr: 1 }}
								>
									Reset
								</Button>
							</Paper>
						)}
					</Box>
					<Divider sx={{ my: 2 }} />
					<DialogActions>
						<Button
							variant="outlined"
							color="warning"
							startIcon={<CloseIcon />}
							onClick={onClose}
							sx={{ height: 40 }}
						>
							<Translate String="Procedure.Button.Close">Close</Translate>
						</Button>
						<Button
							variant="outlined"
							color="success"
							startIcon={<SaveIcon />}
							disabled={isSubmitting}
							autoFocus
							type="submit"
							sx={{ height: 40 }}
						>
							<Translate String="Procedure.Button.Save">Save</Translate>
						</Button>
					</DialogActions>
				</Form>
			)}
		</Formik>
	);
};

interface StepProps {
	values: DocumentImportPlan;
	arrayHelpers: FieldArrayRenderProps;
	checklist: DocumentImportPlan;
}

export const StepOne: FC<StepProps> = ({ values, checklist }) => (
	<>
		<CustomInput
			label="Write a checklist name"
			name="SchemeName"
			type="text"
			id="SchemeName"
			translationString="Procedure.Checklist.New.Wizard.Name.Label"
			fullWidth
			sx={{ height: 40 }}
		/>
		<Divider sx={{ my: 2 }}>
			<Translate String="Procedure.Checklist.New.Wizard.Divider">Or</Translate>
		</Divider>
		<CustomSelect
			name="SchemeName"
			id="SchemeName"
			label="Select a checklist name"
			translationString="Procedure.Checklist.New.Wizard.Select"
			fullWidth
			sx={{ height: 40 }}
		>
			<Option String="Procedure.Checklist.New.Wizard.Title.Choose" value="">
				--- Choose title ---
			</Option>
			{values.Chapters?.map((chapter) => {
				if (chapter.FieldType === 0) {
					return (
						<option key={chapter.ChapterNr} value={chapter.ChapterName}>
							{chapter.ChapterNr} {chapter.ChapterName}
						</option>
					);
				}
			})}
		</CustomSelect>
	</>
);

export const StepTwo: FC<StepProps> = ({ values, arrayHelpers }) => (
	<>
		<Box
			sx={{
				margin: "1rem 0",
				display: "flex",
				gap: "1rem",
				flexWrap: "wrap",
				width: "100%",
			}}
		>
			{values.Chapters?.map(
				(chapter, index) =>
					chapter.FieldType === 0 &&
					chapter.SelectedHeader && (
						<Chip
							color="primary"
							key={index}
							label={`${chapter.ChapterNr} ${chapter.ChapterName.trim()}`}
							onDelete={() => {
								const modifiedChapter = values.Chapters[index];
								modifiedChapter.SelectedHeader = false;
								arrayHelpers.replace(index, modifiedChapter);
							}}
							sx={{ height: 40 }}
						/>
					),
			)}
		</Box>
		<CustomSelect
			name="Chapters"
			id="Chapter"
			label="Choose chapter"
			translationString="Procedure.Checklist.New.Wizard.Chapter.Choose"
			fullWidth
			sx={{ height: 40 }}
			onChange={(e) => {
				const id = +e.target.value;
				const modifiedChapter = values.Chapters[id];
				modifiedChapter.SelectedHeader = true;
				arrayHelpers.replace(id, modifiedChapter);
			}}
		>
			<Option String="Procedure.Checklist.New.Wizard.Chapter.Choose" value="">
				--- Choose chapter ---
			</Option>
			{values.Chapters?.map((chapter, index) => {
				if (!chapter.SelectedHeader && chapter.FieldType === 0) {
					return (
						<option key={chapter.Id} value={index}>
							{chapter.ChapterNr} {chapter.ChapterName}
						</option>
					);
				}
			})}
		</CustomSelect>
	</>
);

export const StepThree: FC<StepProps> = ({ values, arrayHelpers }) => (
	<CustomSelect
		name="Chapters"
		id="Chapter"
		label="Choose a table"
		translationString="Procedure.Checklist.New.Wizard.Table.Choose"
		fullWidth
		sx={{ height: 40 }}
		onChange={(e) => {
			const hasBody = values.Chapters?.find((chapter) => chapter.SelectedBody);
			if (hasBody) {
				hasBody.SelectedBody = false;
				const index = values.Chapters.indexOf(hasBody);
				arrayHelpers.replace(index, hasBody);
			}
			const id = +e.target.value;
			const modifiedChapter = values.Chapters[id];
			modifiedChapter.SelectedBody = true;
			arrayHelpers.replace(id, modifiedChapter);
		}}
	>
		<Option String="Procedure.Checklist.New.Wizard.Table.Choose" value="">
			--- Choose a table ---
		</Option>
		{values.Chapters?.map((chapter, index) => {
			if (chapter.FieldType !== 0) {
				return (
					<option key={index} value={index}>
						Table {chapter.ChapterNr}
					</option>
				);
			}
		})}
	</CustomSelect>
);

export default NewWizardChecklistForm;
